import React, { useCallback, useEffect, useState } from "react";
import axiosClient from "../../lib/axiosClient";
import {
  Calendar,
  Cable,
  MapPin,
  Paperclip,
  Plug,
  Router,
  Spline,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog";

function formatPreviewDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("el-GR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSpiralMeters(v) {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  if (/\s*m$/i.test(s) || s.endsWith("μ")) return s;
  return `${s} m`;
}

function getWorkflowStatusDisplay(status) {
  const s = String(status || "submitted").toLowerCase();
  const base = "px-3 py-1 rounded-full text-xs font-semibold";
  if (s === "approved") {
    return { label: "Ολοκληρώθηκε", badgeClass: `${base} bg-emerald-100 text-emerald-700` };
  }
  if (s === "submitted") {
    return { label: "Καταχωρήθηκε", badgeClass: `${base} bg-slate-100 text-slate-600` };
  }
  if (s === "under_review") {
    return { label: "Σε έλεγχο", badgeClass: `${base} bg-amber-100 text-amber-800` };
  }
  if (s === "needs_correction") {
    return { label: "Απαιτεί διόρθωση", badgeClass: `${base} bg-orange-100 text-orange-800` };
  }
  if (s === "rejected") {
    return { label: "Απορρίφθηκε", badgeClass: `${base} bg-red-100 text-red-700` };
  }
  if (s === "needs_resubmission") {
    return { label: "Απαιτείται νέα υποβολή", badgeClass: `${base} bg-amber-100 text-amber-800` };
  }
  return { label: status || "—", badgeClass: `${base} bg-slate-100 text-slate-600` };
}

function isImageType(ct) {
  return typeof ct === "string" && ct.toLowerCase().startsWith("image/");
}

function isVideoType(ct) {
  return typeof ct === "string" && ct.toLowerCase().startsWith("video/");
}

/** Exact block structure from spec (label + value row). */
function DataBlock({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900">{value || "—"}</div>
    </div>
  );
}

/**
 * My Reports: report metadata + attachments (presigned URLs via GET /api/attachments?report_id=…).
 */
export default function ReportPreviewModal({
  report,
  isOpen,
  onClose,
  backendUrl,
  token,
  employeeDisplayName = "",
}) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const loadAttachments = useCallback(async () => {
    if (!report?.id || !token) return;
    setLoading(true);
    setFetchError("");
    try {
      const res = await axiosClient.get(
        `${backendUrl}/api/attachments?report_id=${encodeURIComponent(String(report.id))}`
      );
      const list = Array.isArray(res.data?.attachments) ? res.data.attachments : [];
      setAttachments(list);
    } catch (e) {
      console.error("[ReportPreviewModal] attachments:", e);
      setFetchError("Δεν ήταν δυνατή η φόρτωση των συνημμένων.");
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [report?.id, backendUrl, token]);

  useEffect(() => {
    if (!isOpen || !report?.id) {
      setAttachments([]);
      setFetchError("");
      return undefined;
    }
    loadAttachments();
    return undefined;
  }, [isOpen, report?.id, loadAttachments]);

  const handleOpenChange = (open) => {
    if (!open) onClose?.();
  };

  const statusInfo = report ? getWorkflowStatusDisplay(report.status) : { label: "—", badgeClass: "" };
  const srNumber = report?.sr || (report?.id ? `Αναφορά #${report.id}` : "—");
  const employeeName =
    (report?.employee_name && String(report.employee_name).trim()) ||
    (employeeDisplayName && String(employeeDisplayName).trim()) ||
    "—";

  const hasRouter = Boolean(report?.router_serial && String(report.router_serial).trim());
  const hasOnt = Boolean(report?.ont_serial && String(report.ont_serial).trim());

  return (
    <Dialog open={Boolean(isOpen && report)} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-2xl"
      >
        {report ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="relative border-b border-slate-100 bg-slate-50 px-8 py-6">
              <div className="absolute top-6 right-20 z-10">
                <span className={statusInfo.badgeClass}>{statusInfo.label}</span>
              </div>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-6 top-6 z-10 h-9 w-9 text-slate-500 hover:bg-slate-200/60 hover:text-slate-900"
                  aria-label="Κλείσιμο"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </Button>
              </DialogClose>

              <DialogTitle asChild>
                <h2 className="pr-40 text-3xl font-extrabold tracking-tight text-slate-900">{srNumber}</h2>
              </DialogTitle>
              <p className="mt-1.5 pr-40 text-sm font-medium text-slate-500">{employeeName}</p>
              <DialogDescription className="sr-only">
                Προεπισκόπηση αναφοράς {srNumber}, {employeeName}
              </DialogDescription>
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-8 border-b border-slate-100 bg-white px-8 py-6 md:grid-cols-3">
              <DataBlock icon={MapPin} label="ΔΙΕΥΘΥΝΣΗ" value={report.address} />
              <DataBlock icon={Calendar} label="ΗΜΕΡΟΜΗΝΙΑ" value={formatPreviewDate(report.date)} />
              <DataBlock icon={Wrench} label="ΤΥΠΟΣ ΡΑΝΤΕΒΟΥ" value={report.appointment_type} />
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-8 border-b border-slate-100 bg-white px-8 py-6 md:grid-cols-3">
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  <Cable className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                  ΊΝΕΣ
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {report.ines_length || "—"}
                </div>
                {hasOnt ? (
                  <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
                    <Wifi className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    ONT #{String(report.ont_serial).trim()}
                  </div>
                ) : null}
                {hasRouter ? (
                  <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
                    <Router className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Router #{String(report.router_serial).trim()}
                  </div>
                ) : null}
              </div>
              <DataBlock icon={Plug} label="ΠΡΙΖΑΚΙΑ" value={report.prizakia} />
              <DataBlock icon={Spline} label="ΣΠΙΡΑΛ" value={formatSpiralMeters(report.spiral_meters)} />
            </div>

            {report.notes ? (
              <div className="border-b border-slate-100 bg-white px-8 py-6">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Σημειώσεις
                </div>
                <p className="whitespace-pre-wrap text-sm font-semibold text-slate-900">{report.notes}</p>
              </div>
            ) : null}

            <div className="bg-white px-8 pb-8 pt-6">
              <div className="mb-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <Paperclip className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                ΣΥΝΗΜΜΕΝΑ
              </div>
              {loading ? (
                <p className="text-sm text-slate-500">Φόρτωση…</p>
              ) : fetchError ? (
                <p className="text-sm text-red-600">{fetchError}</p>
              ) : attachments.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/80 p-8">
                  <Paperclip
                    className="mb-2 h-10 w-10 text-slate-300"
                    strokeWidth={1.25}
                    aria-hidden
                  />
                  <p className="text-sm text-slate-500">Χωρίς συνημμένα</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {attachments.map((att) => {
                    const url = att.downloadUrl;
                    const name = att.filename || "Αρχείο";
                    const ct = att.content_type || "";

                    if (url && isImageType(ct)) {
                      return (
                        <figure key={att.id} className="w-[5.5rem] shrink-0">
                          <img
                            src={url}
                            alt={name}
                            loading="lazy"
                            className="h-20 w-full rounded-lg border border-slate-200 object-cover"
                          />
                          <figcaption className="mt-1 truncate text-center text-xs text-slate-500">
                            {name}
                          </figcaption>
                        </figure>
                      );
                    }

                    if (url && isVideoType(ct)) {
                      return (
                        <figure key={att.id} className="w-[8rem] shrink-0">
                          <video
                            className="h-20 w-full rounded-lg border border-slate-200 object-cover"
                            controls
                            playsInline
                            preload="metadata"
                            src={url}
                          >
                            Ο φυλλομετρητής δεν υποστηρίζει αναπαραγωγή βίντεο.
                          </video>
                          <figcaption className="mt-1 truncate text-center text-xs text-slate-500">
                            {name}
                          </figcaption>
                        </figure>
                      );
                    }

                    return (
                      <div
                        key={att.id}
                        className="flex max-w-[10rem] flex-col rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500"
                      >
                        <span className="truncate font-semibold text-slate-900">{name}</span>
                        <span className="mt-1 text-[11px]">
                          {url
                            ? "Μη υποστηριζόμενος τύπος για προεπισκόπηση."
                            : "Το αρχείο δεν είναι ακόμη διαθέσιμο για λήψη."}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
